import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Button, Card, StatusIndicator } from "../components/ui";
import { ApiUnavailableState, LoadingState, NotConfiguredState } from "../components/states";
import { formatDate } from "../components/billing";
import { AdvancedJson } from "../components/phase3";
import { useAppContext } from "../hooks/useAppContext";
import { useSession } from "../hooks/useSession";
import {
  api,
  apiConfigured,
  ApiError,
  type AccountDeletionPreviewView,
  type AccountDeletionResultView,
  type AccountWorkspaceView,
  type IdentitySummary,
  type IdentityProviderName,
} from "../lib/api";

/**
 * Settings → Linked accounts (Part 1.3).
 *
 * One ODIN account, several ways in. This page is where a customer sees that
 * truth: which providers are attached, which one they used last, and — the part
 * that actually matters when Discord is compromised — which of them could get
 * them back in.
 *
 * Three rules are visible in the UI rather than only enforced on the server, so
 * the customer is never surprised by a refusal:
 *   - the last remaining sign-in method cannot be removed, and its button says so;
 *   - an identity with no provider-VERIFIED email is labelled as unable to
 *     recover the account, instead of letting the customer believe they hold a
 *     second key to the door;
 *   - removing a method needs a recent sign-in, and a 403 says exactly that.
 *
 * Starting a link is a full-page navigation, never a fetch: the provider answers
 * with a redirect the browser must follow, and the session cookie has to ride
 * along so the server can bind the new identity to the signed-in account. The
 * account is read from that cookie server-side — this page never sends a user id.
 *
 * The page also carries the right to erasure. Deleting the account and unlinking
 * a provider are neighbours here because a customer looking for one reasonably
 * looks for the other, but they are different promises and the UI never lets
 * them be confused: unlinking removes one way in, deletion removes the person.
 */

const PROVIDER_LABEL: Record<IdentityProviderName, string> = {
  discord: "Discord",
  google: "Google",
  email: "Email",
};

/** Providers a customer can attach from this page. */
const LINKABLE: readonly ("discord" | "google")[] = ["discord", "google"];

function providerLabel(provider: string): string {
  return PROVIDER_LABEL[provider as IdentityProviderName] ?? provider;
}

/** The banner shown after returning from a provider. */
export function linkOutcomeMessage(params: URLSearchParams): { tone: "ok" | "warn"; text: string } | null {
  const linked = params.get("linked");
  if (linked) {
    return { tone: "ok", text: `${providerLabel(linked)} is now linked to your ODIN account.` };
  }
  const error = params.get("error");
  if (!error) return null;
  const provider = providerLabel(params.get("provider") ?? "that provider");
  if (error === "already_linked") {
    return {
      tone: "warn",
      text: `That ${provider} account is already linked to a different ODIN account. Sign in with it directly, or unlink it there first.`,
    };
  }
  if (error === "link_failed") {
    return { tone: "warn", text: `We couldn't finish linking ${provider}. Nothing was changed — please try again.` };
  }
  return { tone: "warn", text: "That link attempt didn't complete. Nothing was changed." };
}

export function IdentityCard({
  identity,
  onUnlink,
  busy,
}: {
  identity: IdentitySummary;
  onUnlink: (identity: IdentitySummary) => void;
  busy: boolean;
}): JSX.Element {
  const name = providerLabel(identity.provider);
  return (
    <Card title={name}>
      <div style={{ display: "grid", gap: "0.5rem" }}>
        <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
          <StatusIndicator tone="ok" label="Linked" />
          {identity.canRecover ? (
            <span className="count-chip">Can recover your account</span>
          ) : (
            <span className="count-chip count-chip--warn">Cannot recover your account</span>
          )}
        </div>
        <div className="kv-grid">
          <div className="kv">
            <span className="kv__k">Email</span>
            <span className="kv__v">{identity.providerEmail ?? "—"}</span>
          </div>
          <div className="kv">
            <span className="kv__k">Linked</span>
            <span className="kv__v">{formatDate(identity.linkedAt)}</span>
          </div>
          <div className="kv">
            <span className="kv__k">Last used</span>
            <span className="kv__v">{identity.lastUsedAt ? formatDate(identity.lastUsedAt) : "Not yet used"}</span>
          </div>
        </div>
        {identity.canRecover ? null : (
          <p className="muted">
            {name} hasn’t confirmed an email address for this account, so it can’t be used to recover
            access. Link a provider with a verified address as a backup.
          </p>
        )}
        {identity.canUnlink ? (
          <div className="row">
            <Button variant="secondary" onClick={() => onUnlink(identity)} disabled={busy}>
              {busy ? "Removing…" : `Unlink ${name}`}
            </Button>
          </div>
        ) : (
          <>
            <div className="row">
              <Button variant="secondary" disabled aria-describedby={`last-${identity.id}`}>
                Unlink {name}
              </Button>
            </div>
            <p className="muted" id={`last-${identity.id}`}>
              This is your only way to sign in. Link another provider first, and then you can remove
              this one.
            </p>
          </>
        )}
      </div>
    </Card>
  );
}

export function LinkedAccountsView({
  identities,
  onUnlink,
  busyId,
  startUrl,
}: {
  identities: IdentitySummary[];
  onUnlink: (identity: IdentitySummary) => void;
  busyId: string | null;
  startUrl: (provider: "discord" | "google") => string;
}): JSX.Element {
  const linkedProviders = new Set(identities.map((identity) => identity.provider));
  const missing = LINKABLE.filter((provider) => !linkedProviders.has(provider));
  const recoverable = identities.filter((identity) => identity.canRecover).length;
  return (
    <>
      {recoverable === 0 ? (
        <div className="state" role="alert">
          <p className="state__title">No linked account can recover access</p>
          <p>
            None of your sign-in methods has a provider-verified email address. If you lose access to
            them, we can’t confirm the account is yours. Linking Google is the quickest fix.
          </p>
        </div>
      ) : null}
      <div className="card-grid">
        {identities.map((identity) => (
          <IdentityCard
            key={identity.id}
            identity={identity}
            onUnlink={onUnlink}
            busy={busyId === identity.id}
          />
        ))}
      </div>
      {missing.length > 0 ? (
        <Card title="Add another way to sign in">
          <p>
            Linking a second provider means one ODIN account, one subscription and one set of
            workspaces — however you choose to sign in.
          </p>
          <div className="row" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
            {missing.map((provider) => (
              <a key={provider} className="btn btn--secondary" href={startUrl(provider)}>
                Link {PROVIDER_LABEL[provider]}
              </a>
            ))}
          </div>
        </Card>
      ) : null}
    </>
  );
}

// ── Delete account (right to erasure) ───────────────────────────────────────
/**
 * The same three rules that shape workspace deletion, applied to the person.
 *
 *   - The consequences are written by the server and rendered verbatim. Only the
 *     server knows which workspaces would go, which would carry on without them,
 *     and which Discord servers are attached to each.
 *   - The browser never decides that a deletion was confirmed. The typed address
 *     is sent, and the server refuses anything that does not match. The local
 *     check is deliberately no stricter than the server's, so a customer who
 *     typed the right thing never faces a dead button.
 *   - Deleting an account does not remove ODIN from anyone's Discord server. The
 *     server says so in `consequences`; the receipt repeats it, because that is
 *     the single fact a customer is most likely to have assumed the other way.
 *
 * There is no undo, and the panel says so before the button is reachable rather
 * than after it is pressed.
 */
export function accountConfirmationMatches(typed: string, phrase: string): boolean {
  return typed.trim().toLowerCase() === phrase.trim().toLowerCase() && phrase.trim().length > 0;
}

/** A customer-facing sentence for a failed deletion, preferring the server's own words. */
export function accountDeleteErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === "recent_auth_required") {
      return "For your security, sign in again before deleting your account. Nothing was deleted.";
    }
    if (error.code === "rate_limited") {
      return "That's several delete attempts in a short time. Nothing was deleted — wait a few minutes and try again.";
    }
    // conflict / invalid_request / not_found each carry a server-written reason
    // naming the specific obstacle, which is more useful than anything this file
    // could say.
    return error.message;
  }
  return "We couldn't delete your account, and nothing was changed. Please try again.";
}

/** One line per workspace, so "your workspaces" is never an abstraction. */
export function WorkspaceDispositionList({
  title,
  workspaces,
  emptyLabel,
}: {
  title: string;
  workspaces: AccountWorkspaceView[];
  emptyLabel: string;
}): JSX.Element {
  return (
    <>
      <h4 className="ax-confirm__subtitle">{title}</h4>
      {workspaces.length === 0 ? (
        <p className="muted">{emptyLabel}</p>
      ) : (
        <ul className="ax-confirm__list">
          {workspaces.map((workspace) => (
            <li key={workspace.tenantId}>
              <strong>{workspace.name}</strong>
              {workspace.guilds.length > 0
                ? ` — Discord servers: ${workspace.guilds.map((g) => g.name).join(", ")}`
                : " — no Discord servers connected"}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export function DeleteAccountPanel({
  preview,
  typed,
  onTypedChange,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  preview: AccountDeletionPreviewView;
  typed: string;
  onTypedChange: (next: string) => void;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  const matches = accountConfirmationMatches(typed, preview.confirmationPhrase);
  const blocked = !preview.canDelete;
  return (
    <section className="ax-confirm" aria-label="Delete your ODIN account">
      <h3 className="ax-confirm__title">Delete your ODIN account?</h3>

      <ul className="ax-confirm__list">
        {preview.consequences.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>

      <WorkspaceDispositionList
        title="Workspaces that would be deleted with your account"
        workspaces={preview.workspacesToDelete}
        emptyLabel="None — no workspace would be removed."
      />
      <WorkspaceDispositionList
        title="Workspaces that would carry on without you"
        workspaces={preview.workspacesToLeave}
        emptyLabel="None — you are not sharing a workspace with anyone."
      />

      {preview.preserved.length > 0 ? (
        <>
          <h4 className="ax-confirm__subtitle">What ODIN keeps</h4>
          <ul className="ax-confirm__list">
            {preview.preserved.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        </>
      ) : null}

      {preview.requiresRecentSignIn ? (
        <p className="ax-metric__foot" role="status">
          You signed in a while ago. Sign in again before confirming, so we know it is you asking.{" "}
          <a className="ax-card__link" href="/login?next=%2Fapp%2Fsettings%2Flinked-accounts">
            Sign in again
          </a>
        </p>
      ) : null}

      {blocked ? (
        <div role="alert">
          <h4 className="ax-confirm__subtitle">Your account can&apos;t be deleted yet</h4>
          <ul className="ax-confirm__list">
            {preview.blockers.map((blocker) => (
              <li key={blocker.code}>
                <strong>{blocker.message}</strong> {blocker.resolution}
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <label className="ax-confirm__check" style={{ display: "grid", gap: "0.35rem" }}>
          <span>
            Type <strong>{preview.confirmationPhrase}</strong> to confirm.
          </span>
          <input
            className="field__input"
            value={typed}
            onChange={(e) => onTypedChange(e.target.value)}
            disabled={busy}
            autoComplete="off"
            aria-label={`Type ${preview.confirmationPhrase} to confirm deleting your account`}
          />
        </label>
      )}

      {error ? (
        <p className="ax-confirm__error" role="alert">
          {error}
        </p>
      ) : null}

      <div className="ax-rail-actions" style={{ marginTop: "0.75rem" }}>
        <button
          type="button"
          className="ax-btn ax-btn--danger"
          onClick={onConfirm}
          disabled={busy || blocked || !matches}
        >
          {busy ? "Deleting…" : "Permanently delete my account"}
        </button>
        <button type="button" className="ax-btn ax-btn--secondary" onClick={onCancel} disabled={busy}>
          Keep my account
        </button>
      </div>
    </section>
  );
}

type DeletePreviewState =
  | { kind: "closed" }
  | { kind: "loading" }
  | { kind: "ready"; data: AccountDeletionPreviewView }
  | { kind: "error"; detail: string };

export function DeleteAccountCard({
  preview,
  typed,
  onTypedChange,
  busy,
  error,
  onOpen,
  onConfirm,
  onCancel,
}: {
  preview: DeletePreviewState;
  typed: string;
  onTypedChange: (next: string) => void;
  busy: boolean;
  error: string | null;
  onOpen: () => void;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  return (
    <Card title="Delete your account">
      <p className="muted">
        You can ask us to erase your ODIN account at any time. Before anything happens we&apos;ll show
        you exactly which workspaces and Discord servers that touches, and what accounting law
        requires us to keep.
      </p>
      {preview.kind === "closed" ? (
        <div className="row" style={{ marginTop: "0.5rem" }}>
          <Button variant="secondary" onClick={onOpen}>
            Show me what deleting my account would do
          </Button>
        </div>
      ) : null}
      {preview.kind === "loading" ? <LoadingState label="Checking what this would affect…" /> : null}
      {preview.kind === "error" ? (
        <>
          <p className="ax-confirm__error" role="alert">
            {preview.detail}
          </p>
          <div className="row" style={{ marginTop: "0.5rem" }}>
            <Button variant="secondary" onClick={onOpen}>
              Try again
            </Button>
          </div>
        </>
      ) : null}
      {preview.kind === "ready" ? (
        <DeleteAccountPanel
          preview={preview.data}
          typed={typed}
          onTypedChange={onTypedChange}
          busy={busy}
          error={error}
          onConfirm={onConfirm}
          onCancel={onCancel}
        />
      ) : null}
    </Card>
  );
}

export function AccountDeletedSummary({
  result,
}: {
  result: AccountDeletionResultView;
}): JSX.Element {
  const { receipt } = result;
  return (
    <>
      <PageHeader title="Your account is deleted" />
      <Card title="Erasure complete">
        <StatusIndicator tone="ok" label="Deleted" />
        <p style={{ marginTop: "0.5rem" }}>
          Your name, email address and every way of signing in have been removed. You are signed out
          of {receipt.sessionsRevoked === 1 ? "one device" : `${receipt.sessionsRevoked} devices`}, and
          this account cannot be signed in to again.
        </p>
        <ul className="ax-confirm__list">
          <li>
            {receipt.tenantsDeleted.length === 0
              ? "No workspace had to be removed."
              : `${receipt.tenantsDeleted.length} ${
                  receipt.tenantsDeleted.length === 1 ? "workspace was" : "workspaces were"
                } deleted with the account.`}
          </li>
          <li>
            {receipt.membershipsRemoved === 0
              ? "You were not a member of a shared workspace."
              : `You were removed from ${receipt.membershipsRemoved} ${
                  receipt.membershipsRemoved === 1 ? "workspace" : "workspaces"
                }. Anything shared with other people carries on without you.`}
          </li>
          <li>
            {receipt.identitiesRemoved === 1
              ? "One sign-in method was removed."
              : `${receipt.identitiesRemoved} sign-in methods were removed.`}
          </li>
        </ul>
        <p className="ax-metric__foot">
          ODIN is still in whatever Discord servers it was in. Deleting an account never removes the
          bot from a Discord server — that is a separate action taken from Discord itself, so nobody
          else&apos;s community changes because you left.
        </p>
        <p className="ax-metric__foot">
          Invoices and payment records are kept because accounting law requires OKMedia to keep them.
          Everything else about you is gone.
        </p>
        <div className="row" style={{ marginTop: "0.75rem" }}>
          <a className="ax-btn ax-btn--primary" href="/">
            Back to odinforge.io
          </a>
        </div>
        <AdvancedJson title="Advanced — what was removed" value={receipt.purged} />
      </Card>
    </>
  );
}

/**
 * The way in to the duplicate-accounts screen, for the people who have one.
 *
 * It lives here because this is the page about "which sign-in methods belong to
 * this account" — the same question one level up. An operator who has just been
 * told "my Google login shows a different, empty account" is already on this
 * page; making them know a URL by heart would be the difference between the
 * merge engine existing and the merge engine being used.
 *
 * It renders for platform operators only. That is a courtesy, not the control:
 * the screen and all four API routes check the role themselves, so hiding the
 * link removes an advertisement, never a protection.
 */
export function OperatorMergeLink({ platformRole }: { platformRole: string }): JSX.Element | null {
  if (platformRole !== "ADMIN" && platformRole !== "OWNER") return null;
  return (
    <Card title="Duplicate accounts (platform operators)">
      <p className="muted">
        When one person has ended up with two ODIN accounts — signed in with Discord once, Google
        another time — this is where the pair is reviewed and, if the evidence holds, joined into one.
        Opening it changes nothing.
      </p>
      <div className="row" style={{ marginTop: "0.5rem" }}>
        <Link className="btn btn--secondary" to="/app/account-merges">
          Review duplicate accounts
        </Link>
      </div>
    </Card>
  );
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; data: IdentitySummary[] }
  | { kind: "unavailable"; detail?: string };

export function LinkedAccounts(): JSX.Element {
  const [params] = useSearchParams();
  const ctx = useAppContext();
  const { state: session } = useSession();
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [busyId, setBusyId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * Terminal, and deliberately outside `LoadState`.
   *
   * Once the account is gone every other call on this page 401s, and re-running
   * the loader would replace the receipt with "we couldn't reach the API" — the
   * one screen a customer must be able to read after an irreversible action.
   */
  const [deleted, setDeleted] = useState<AccountDeletionResultView | null>(null);
  const [deletePreview, setDeletePreview] = useState<DeletePreviewState>({ kind: "closed" });
  const [typed, setTyped] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!apiConfigured || deleted) return;
    setState({ kind: "loading" });
    api.identities
      .list()
      .then((res) => setState({ kind: "ready", data: res.identities }))
      .catch((error: unknown) =>
        setState({ kind: "unavailable", detail: error instanceof Error ? error.message : undefined }),
      );
  }, [deleted]);

  useEffect(() => {
    load();
  }, [load]);

  const onUnlink = useCallback(async (identity: IdentitySummary) => {
    setBusyId(identity.id);
    setNotice(null);
    try {
      const res = await api.identities.unlink(identity.id);
      setState({ kind: "ready", data: res.identities });
      setNotice(`${providerLabel(identity.provider)} is no longer linked to your ODIN account.`);
    } catch (error) {
      if (error instanceof ApiError && error.code === "recent_auth_required") {
        setNotice("For your security, sign in again before changing your linked accounts.");
      } else if (error instanceof ApiError && error.code === "last_identity") {
        setNotice("That's your only way to sign in, so it can't be removed. Link another provider first.");
      } else {
        setNotice(error instanceof ApiError ? error.message : "Couldn't unlink that account. Please retry.");
      }
    } finally {
      setBusyId(null);
    }
  }, []);

  const onOpenDeletePreview = useCallback(async () => {
    setDeletePreview({ kind: "loading" });
    setDeleteError(null);
    setTyped("");
    try {
      setDeletePreview({ kind: "ready", data: await api.account.deletePreview() });
    } catch (error) {
      setDeletePreview({
        kind: "error",
        detail:
          error instanceof ApiError
            ? error.message
            : "We couldn't work out what deleting your account would affect, so we're not offering the button.",
      });
    }
  }, []);

  const onConfirmDelete = useCallback(async () => {
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      const result = await api.account.delete(typed);
      setDeleted(result);
      // The selected workspace is persisted in this browser. Leaving it there
      // would point the next person at a workspace that no longer exists.
      ctx.clearWorkspace();
    } catch (error) {
      setDeleteError(accountDeleteErrorMessage(error));
    } finally {
      setDeleteBusy(false);
    }
  }, [ctx, typed]);

  const onCancelDelete = useCallback(() => {
    setDeletePreview({ kind: "closed" });
    setTyped("");
    setDeleteError(null);
  }, []);

  const outcome = linkOutcomeMessage(params);

  if (deleted) {
    return <AccountDeletedSummary result={deleted} />;
  }

  if (!apiConfigured) {
    return (
      <>
        <PageHeader title="Linked accounts" />
        <NotConfiguredState title="Control API URL not configured">
          Set <code>PUBLIC_API_URL</code> when building the dashboard to manage sign-in methods.
        </NotConfiguredState>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Linked accounts">
        <p>
          Every provider below signs you in to the same ODIN account — the same subscription, the same
          workspaces and the same BALDER history. <Link to="/app/settings">Back to settings</Link>
        </p>
      </PageHeader>
      {outcome ? (
        <p role="status" className={outcome.tone === "ok" ? "muted" : "state__title"}>
          {outcome.text}
        </p>
      ) : null}
      {notice ? (
        <p role="status" className="muted">
          {notice}
        </p>
      ) : null}
      {state.kind === "loading" ? <LoadingState label="Loading linked accounts…" /> : null}
      {state.kind === "unavailable" ? (
        <div className="state" role="alert">
          <ApiUnavailableState detail={state.detail} />
          <Button variant="secondary" onClick={load}>
            Retry
          </Button>
        </div>
      ) : null}
      {state.kind === "ready" ? (
        <LinkedAccountsView
          identities={state.data}
          onUnlink={onUnlink}
          busyId={busyId}
          startUrl={(provider) => api.identities.startUrl(provider)}
        />
      ) : null}
      {session.kind === "authenticated" ? <OperatorMergeLink platformRole={session.me.platformRole} /> : null}
      <div className="card-grid">
        <DeleteAccountCard
          preview={deletePreview}
          typed={typed}
          onTypedChange={setTyped}
          busy={deleteBusy}
          error={deleteError}
          onOpen={() => void onOpenDeletePreview()}
          onConfirm={() => void onConfirmDelete()}
          onCancel={onCancelDelete}
        />
      </div>
    </>
  );
}
